import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { TRACK_EVENT, Typography } from '@tkww/the-bash-frontend';
import { Box } from '@mui/material';

import Grid from '@mui/material/Grid';
import Image from 'components/Image';
import Tag from 'components/Tag';
import Divider from 'components/Divider';
import InlineList from 'components/InlineList';
import jump from 'helpers/jump';
import PhotoCopy from 'components/PhotoCopy';
import getResizeImageUrl from 'helpers/media/getResizeImageUrl';

export const getThemeHeaderImages = (imageUrl) => ({
  mobile: getResizeImageUrl(imageUrl, 379, 'h', true),
  desktop: getResizeImageUrl(imageUrl, 851, 'h', true),
});

const ThemeHeader = (props) => {
  const { theme, showPinterestButton } = props;
  const dispatch = useDispatch();
  const altText = theme.image.altText
    ? theme.image.altText
    : `${theme.title} party themed inspiration and ideas`;

  const handleIngredientClick = (ingredient) => {
    dispatch({
      type: TRACK_EVENT,
      name: 'Menu Interaction',
      properties: {
        themeName: theme.title,
        themeId: theme.id,
        clickTarget: ingredient.title,
      },
    });
    jump(`#${ingredient.slug}`);
  };

  const imageUrls = getThemeHeaderImages(theme.image.url);

  return (
    <Grid
      container
      sx={{
        flexWrap: { xs: 'wrap', lg: 'nowrap' },
        alignItems: 'center',
        '& .photo': {
          '& img': {
            width: '100%',
            minHeight: { xs: 197, md: 510 },
          },
        },
      }}
      align="center"
      justifyContent="center"
      data-testid="theme-header"
      spacing={0}
    >
      <Grid item md={12} lg={9}>
        <PhotoCopy className="photo" title={theme.image.credit} titleUrl={theme.image.creditUrl}>
          <Image
            src={imageUrls.desktop}
            srcMobile={imageUrls.mobile}
            alt={altText}
            pinterestDescription={theme.image.pinterestDescription}
            showPinterestButton={showPinterestButton}
            lazyLoad={false}
          />
        </PhotoCopy>
      </Grid>
      <Grid
        item
        sx={{
          marginTop: { xs: '-26px !important', md: 0 },
          marginLeft: { lg: '-15%' },
          zIndex: { lg: 200 },
        }}
        xs={10}
        sm={10}
        lg={5}
      >
        <Box
          sx={{
            marginTop: { xs: '37px', md: 0 },
          }}
        />
        <Box
          sx={(theme) => ({
            textAlign: 'center',
            backgroundColor: theme.palette.primary.contrastText,
            padding: 4,
            borderRadius: '3px',
            border: `solid 1px ${theme.palette.grey[200]}`,
          })}
        >
          <Typography
            variant="h1"
            sx={(theme) => ({
              color: theme.palette.secondary.main,
              margin: theme.spacing(0, 0, 1),
            })}
          >
            <span data-qa="theme-title">{theme.title}</span> Party Theme
          </Typography>
          <Typography>{theme.shortDescription}</Typography>
          <Divider />
          <InlineList align="center">
            {theme.ingredients.map((ingredient, i) => (
              <Tag
                data-testId={`theme-header-ingredient-${i}`}
                key={ingredient.slug}
                onClick={() => handleIngredientClick(ingredient)}
                name={ingredient.title}
              />
            ))}
          </InlineList>
        </Box>
      </Grid>
    </Grid>
  );
};

ThemeHeader.propTypes = {
  theme: PropTypes.shape({
    title: PropTypes.string,
    ingredients: PropTypes.arrayOf(PropTypes.object),
  }),
  showPinterestButton: PropTypes.bool,
};

ThemeHeader.defaultProps = {
  theme: {},
  showPinterestButton: false,
};

export default ThemeHeader;
